import React, {
  useEffect,
  useState,
  forwardRef,
  Suspense,
  HTMLAttributes,
} from 'react';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  Typography,
  Box,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Button,
  Grid,
  Stack,
  FormControl,
  PopperProps,
} from '@mui/material';

import {
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning,
} from '@mui/icons-material';
import { Controller, useFieldArray } from 'react-hook-form';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { useCreateOrUpdateGenePanel, useGenePanelFormHandler } from './hooks';
import { GenePayload } from '@/zod_schemas';
import CustomDialog from '@/components/Dialog/CustomDialog';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useDeleteGene } from '@/api/genes/genes';
import PageHeader from '@/components/PageHeader/PageHeader';
import LoadingState from '@/components/LoadingState';

// Define consistent column widths to ensure alignment
const gridTemplateColumns = '150px 100px 1fr 150px';

// Custom Popper component moved outside to prevent recreation on every render
const CustomPopperComponent = (props: PopperProps) => (
  <Popper
    {...props}
    style={{ width: 'auto', minWidth: '700px', zIndex: 1300 }}
    placement="bottom-start"
  />
);

// Custom Listbox component moved outside to prevent recreation on every render
const CustomListboxComponent = forwardRef<
  HTMLUListElement,
  HTMLAttributes<HTMLUListElement>
>((props, ref) => {
  const children = React.Children.toArray(props.children);

  return (
    <ul {...props} ref={ref} style={{ padding: 0, margin: 0, width: '100%' }}>
      {/* Table Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          alignItems: 'center',
          px: 2,
          py: 1,
          fontWeight: 'bold',
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: '100%',
          '& > *': {
            padding: '0 2px',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>Symbol</Box>
        <Box sx={{ width: '100%' }}>HGNC ID</Box>
        <Box sx={{ width: '100%' }}>Name</Box>
        <Box sx={{ width: '100%' }}>RefSeq</Box>
      </Box>
      {/* Rows Container */}
      <Box sx={{ width: '100%' }}>{children}</Box>
    </ul>
  );
});

const GenePanel = () => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxList, setMaxList] = useState(25);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedValue, setSelectedValue] = useState<GenePayload | null>(null);
  const [saveLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const closeDialog = () => {
    setDialogOpen(false);
    setDeleteId([]);
  };

  const askDelete = (id: string) => {
    setDeleteId([id]);
    setDialogOpen(true);
  };

  const deleteGene = useDeleteGene();

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    register,
    mode,
    panelId,
  } = useGenePanelFormHandler();

  const onSubmit = useCreateOrUpdateGenePanel(mode, panelId);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'genes',
    keyName: '_key',
  });

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      if (panelId) {
        await deleteGene.mutateAsync({ panelId, id: deleteId.toString() });
        enqueueSnackbar('Gene panel deleted', { variant: 'success' });
        closeDialog();

        const idx = fields.findIndex(
          (f) => f.gene.hgnc_id === deleteId.toString(),
        );
        if (idx !== -1) remove(idx);
      } else {
        enqueueSnackbar('Failed to detect panel ID', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const watchedValues = watch();

  const fetchGenes = async (query: string, max: number = 25) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        'https://clinicaltables.nlm.nih.gov/api/genes/v4/search',
        {
          params: {
            terms: query,
            df: 'hgnc_id,symbol,name,refseq_accession',
            maxList: max,
          },
        },
      );

      const [total, , , rows] = response.data;
      setTotalResults(total);

      const formatted = rows.map((item: string[]) => ({
        hgnc_id: item[0],
        symbol: item[1],
        name: item[2],
        refseq_accession: item[3] ? [item[3]] : [],
      }));

      const showMore = max === 25 && total > 25;
      const finalOptions = showMore
        ? [...formatted, { isFooter: true }]
        : formatted;

      setOptions(finalOptions);
    } catch (err: unknown) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchGenes(inputValue, maxList);
    }, 300);
    return () => clearTimeout(delay);
  }, [inputValue, maxList]);

  const handleAddGene = (opt: {
    hgnc_id: string;
    symbol: string;
    name: string;
    refseq_accession: string[];
    isFooter?: boolean;
  }) => {
    // ignore the footer “See more…” option
    if (!opt || opt.isFooter) return;

    // de-dup by HGNC ID against the current field array (new structure)
    const isAlreadyAdded = fields.some((f) => f.gene?.hgnc_id === opt.hgnc_id);
    if (!isAlreadyAdded) {
      append({
        notes: '',
        gene: {
          hgnc_id: opt.hgnc_id,
          symbol: opt.symbol,
          name: opt.name,
          refseq_accession: opt.refseq_accession ?? [],
        },
      });
    }

    // reset UI bits
    setInputValue('');
    setSelectedValue(null);
    setOptions([]);
  };

  // clear all rows
  const handleClearAll = () => {
    remove(fields.map((_, i) => i));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        boxSizing: 'border-box',
        bgcolor: 'transparent',
      }}
    >
      <Box
        sx={{
          maxWidth: 1440,
          mx: 'auto',
          p: { xs: 2, md: 3 },
        }}
      >
        <PageHeader
          eyebrow="Configuration"
          title={mode === 'create' ? 'Create gene panel' : 'Edit gene panel'}
          description="Define the curated genes used to support variant review and panel-based filtering."
          compact
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            {/* Panel Information Section */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h3" sx={{ mb: 2.5 }}>
                Panel details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <Controller
                      name="name"
                      control={control}
                      rules={{
                        required: 'Panel name is required',
                        minLength: {
                          value: 3,
                          message: 'Panel name must be at least 3 characters',
                        },
                        maxLength: {
                          value: 100,
                          message:
                            'Panel name must be less than 100 characters',
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Panel Name *"
                          variant="outlined"
                          fullWidth
                          placeholder="e.g. Breast Cancer Panel"
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          sx={{ mb: 1 }}
                          {...register('name')}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <InfoIcon
                      color="primary"
                      sx={{ mr: 1, fontSize: '1.2rem' }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Panel Summary: {fields.length} genes selected
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {watchedValues.name
                      ? `"${watchedValues.name}"`
                      : 'Unnamed Panel'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: 'Description is required',
                      minLength: {
                        value: 10,
                        message: 'Description must be at least 10 characters',
                      },
                      maxLength: {
                        value: 500,
                        message: 'Description must be less than 500 characters',
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Panel Description *"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Describe the clinical purpose and scope of this gene panel..."
                        error={!!errors.description}
                        helperText={
                          errors.description?.message ||
                          `${field.value?.length || 0}/500 characters`
                        }
                        onChange={(e) => {
                          field.onChange(e);
                          console.log('Description changed:', e.target.value);
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Search Section */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h3" sx={{ mb: 0.5 }}>
                Add genes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Search the gene catalogue and add genes to this curated panel.
              </Typography>

              <Autocomplete
                disablePortal
                filterOptions={(x) => x}
                options={options}
                loading={loading}
                value={selectedValue}
                getOptionLabel={(option) =>
                  option.isFooter ? '' : option.symbol
                }
                isOptionEqualToValue={(option, value) =>
                  option.hgnc_id === value.hgnc_id
                }
                onInputChange={(_, value) => {
                  setInputValue(value);
                  setMaxList(25);
                }}
                onChange={(_, newValue) => {
                  if (newValue && !newValue.isFooter) {
                    handleAddGene({
                      hgnc_id: newValue.hgnc_id,
                      symbol: newValue.symbol,
                      name: newValue.name,
                      // normalize to [] when undefined
                      refseq_accession: newValue.refseq_accession ?? [],
                    });
                  }
                }}
                onClose={() => {
                  // Additional cleanup when dropdown closes
                  setSelectedValue(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Gene Symbol or Name"
                    placeholder="e.g. BRCA1, TP53, EGFR"
                    variant="outlined"
                    helperText={
                      inputValue.length >= 2
                        ? `${totalResults} results found`
                        : 'Enter at least 2 characters to search'
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxComponent={CustomListboxComponent}
                PopperComponent={CustomPopperComponent}
                renderOption={(props, option) => {
                  if (option.isFooter) {
                    return (
                      <li
                        {...props}
                        style={{ justifyContent: 'center', padding: '8px' }}
                      >
                        <Typography
                          color="primary"
                          sx={{ cursor: 'pointer' }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setMaxList(500);
                          }}
                        >
                          See more items...
                        </Typography>
                      </li>
                    );
                  }

                  const isAlreadySelected = fields.some(
                    (g) => g.gene?.hgnc_id === option.hgnc_id,
                  );

                  return (
                    <li {...props} style={{ padding: 0, width: '100%' }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns,
                          px: 2,
                          py: 1,
                          columnGap: 0,
                          width: '100%',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          bgcolor: isAlreadySelected
                            ? 'primary.light'
                            : 'transparent',
                          '&:hover': {
                            bgcolor: isAlreadySelected
                              ? 'primary.light'
                              : 'action.hover',
                          },
                          '& > *': {
                            padding: '0 2px',
                          },
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography noWrap fontWeight="bold">
                            {option.symbol}
                            {isAlreadySelected && (
                              <Chip
                                label="Added"
                                size="small"
                                color="primary"
                                sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ width: '100%' }}>
                          <Typography noWrap>{option.hgnc_id}</Typography>
                        </Box>
                        <Box sx={{ width: '100%' }}>
                          <Typography noWrap>{option.name}</Typography>
                        </Box>
                        <Box sx={{ width: '100%' }}>
                          <Typography noWrap>
                            {option.refseq_accession}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  );
                }}
                renderTags={() => null}
                sx={{ width: '100%' }}
              />
            </Paper>

            {/* Selected Genes Table */}
            <Paper variant="outlined">
              <Box sx={{ p: { xs: 2, md: 3 }, pb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="h3">
                    Selected Genes ({fields.length})
                  </Typography>
                  {fields.length > 0 && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'error.main',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': {
                          color: 'error.dark',
                        },
                      }}
                      onClick={handleClearAll}
                    >
                      Clear All
                    </Typography>
                  )}
                </Box>

                {fields.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No genes selected. Use the search above to find and add
                    genes to your panel.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table
                      sx={{ minWidth: 650 }}
                      aria-label="selected genes table"
                    >
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'background.default' }}>
                          <TableCell
                            sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                          >
                            Gene Symbol
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                          >
                            HGNC ID
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                          >
                            Gene Name
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                          >
                            RefSeq Accession
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 'bold',
                              color: 'text.secondary',
                              width: 80,
                            }}
                          >
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields.map((item) => (
                          <TableRow
                            key={item.id}
                            sx={{
                              '&:nth-of-type(odd)': {
                                bgcolor: 'background.default',
                              },
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color="primary"
                              >
                                {item.gene.symbol}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {item.gene.hgnc_id}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 300 }}
                              >
                                {item.gene.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {/* Show first refseq or join all */}
                                {item.gene.refseq_accession?.join(', ') ?? '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => askDelete(item.gene.hgnc_id)}
                                sx={{ '&:hover': { bgcolor: 'error.light' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              {/* <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  console.log('Current form values:', getValues());
                  console.log('Current selected genes:', fields);
                  console.log('Form validation state:', { isValid, errors });
                }}
              >
                Debug Form
              </Button> */}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saveLoading || !isValid || fields.length === 0}
              >
                {saveLoading ? 'Saving...' : 'Save Gene Panel'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>

      {/* Single global dialog */}
      <CustomDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={confirmDelete}
        confirmButtonProps={{
          color: 'error',
          // disabled: deleteGenePanel.isPending,
        }}
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <span>Delete Gene</span>
          </Stack>
        }
      >
        <div>Are you sure you want to delete this gene?</div>
      </CustomDialog>
    </Paper>
  );
};

const GenePanelWithSuspense = () => (
  <Suspense fallback={<LoadingState label="Loading gene panel…" />}>
    <GenePanel />
  </Suspense>
);

export default GenePanelWithSuspense;
